<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useRestaurantsStore } from '@/stores/restaurants'
import { useOrdersStore } from '@/stores/orders'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { usePermissions } from '@/composables/usePermissions'
import apiInstance from '@/utils/api'
import DashboardChartModal from '@/components/DashboardChartModal.vue'

const router = useRouter()
const restaurantsStore = useRestaurantsStore()
const ordersStore = useOrdersStore()
const authStore = useAuthStore()
const { t, locale } = useI18n()
const { hasUserAnyRole } = usePermissions()

// Format date to YYYY-MM-DD without timezone issues
const getFormattedDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Given a date range, return the immediately preceding range of the same length.
const getPreviousPeriodRange = (
  start: string,
  end: string
): [string, string] => {
  const dayMs = 24 * 60 * 60 * 1000

  const startDate = new Date(start)
  const endDate = new Date(end)

  const selectedDays = Math.round((endDate.getTime() - startDate.getTime()) / dayMs) + 1

  // Check if range is a complete month
  const isFirstDayOfMonth = startDate.getDate() === 1

  const daysInSelectedMonth = new Date(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    0
  ).getDate()

  const isFullMonth =
    isFirstDayOfMonth && selectedDays === daysInSelectedMonth

  if (isFullMonth) {
    const prevMonthStart = new Date(
      startDate.getFullYear(),
      startDate.getMonth() - 1,
      1
    )

    const prevMonthEnd = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      0
    )

    return [
      getFormattedDate(prevMonthStart),
      getFormattedDate(prevMonthEnd),
    ]
  }

  const prevEnd = new Date(startDate.getTime() - dayMs)
  const prevStart = new Date(
    prevEnd.getTime() - (selectedDays - 1) * dayMs
  )

  return [getFormattedDate(prevStart), getFormattedDate(prevEnd)]
}

// Format a YYYY-MM-DD string as DD.MM.YYYY for display
const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}.${month}.${year}`
}

const formatRangeLabel = (start: string, end: string) =>
  start && end ? `${formatDisplayDate(start)} - ${formatDisplayDate(end)}` : ''

// Date range utilities
const getDateRanges = () => {
  const today = new Date()

  return {
    'last-7-days': {
      label: t('dashboard.last_7_days'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)),
      endDate: getFormattedDate(today),
    },
    'last-week': {
      label: t('dashboard.last_week'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 7)),
      endDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 1)),
    },
    'current-week': {
      label: t('dashboard.current_week'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay())),
      endDate: getFormattedDate(today),
    },
    'last-30-days': {
      label: t('dashboard.last_30_days'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)),
      endDate: getFormattedDate(today),
    },
    'last-month': {
      label: t('dashboard.last_month'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      endDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    },
    'current-month': {
      label: t('dashboard.current_month'),
      startDate: getFormattedDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      endDate: getFormattedDate(today),
    },
  }
}

const dateRanges = ref(getDateRanges())

const initialRange = dateRanges.value['last-7-days']
const dateRange = ref<[string, string]>([
  initialRange.startDate,
  initialRange.endDate,
])

// Element Plus DatePicker shortcuts
const shortcuts = computed(() =>
  Object.entries(dateRanges.value).map(([, range]) => ({
    text: range.label,
    value: () => {
      const start = new Date(range.startDate)
      const end = new Date(range.endDate)
      return [start, end]
    },
  }))
)
const isLoadingStats = ref(false)
const statsError = ref<string>('')

interface RestaurantStat {
  restaurant_id: number | null
  restaurant_name: string
  total_orders: number
  failed_transaction: number
  failed_transaction_ratio: string
}

type RestaurantStatWithId = RestaurantStat & {
  restaurant_id: number
}

const createEmptyStats = () => ({
  total_orders: 0,
  total_revenue: 0,
  failed_transaction: 0,
  failed_transaction_ratio: '0%',
  average_check: 0,
  restaurant_wise_stats: [] as RestaurantStat[],
})

// Dashboard stats from API (current period)
const dashboardData = ref(createEmptyStats())

// Dashboard stats for the previous period (same length, immediately before current)
const previousData = ref(createEmptyStats())

// Previous period date range (reactive, derived from the selected range)
const previousDateRange = computed<[string, string]>(() => {
  if (!dateRange.value || dateRange.value.length !== 2) return ['', '']
  return getPreviousPeriodRange(dateRange.value[0], dateRange.value[1])
})

const currentPeriodLabel = computed(() =>
  dateRange.value?.length === 2 ? formatRangeLabel(dateRange.value[0], dateRange.value[1]) : ''
)
const previousPeriodLabel = computed(() =>
  formatRangeLabel(previousDateRange.value[0], previousDateRange.value[1])
)

// Absolute difference (current − previous), signed and formatted
const formatDiff = (diff: number, { decimals = 0, suffix = '' } = {}) => {
  const sign = diff > 0 ? '+' : ''
  return `${sign}${diff.toFixed(decimals)}${suffix}`
}

// Parse a percentage string like "23.68%" into a number
const parsePercent = (value: string) => parseFloat(value) || 0

// Pill color for a difference, given which direction is "good" for the metric
const diffPillClass = (diff: number, betterWhen: 'higher' | 'lower') => {
  if (diff === 0) return 'text-gray-500'
  const isGood = betterWhen === 'higher' ? diff > 0 : diff < 0
  return isGood ? 'text-green-700' : 'text-red-700'
}

// Previous period restaurant stats keyed by restaurant_id for quick lookup
const previousStatsMap = computed(() => {
  const map = new Map<number | null, RestaurantStat>()
  for (const restaurant of previousData.value.restaurant_wise_stats || []) {
    map.set(restaurant.restaurant_id, restaurant)
  }
  return map
})

// Attach previous period values and absolute differences to a restaurant stat
const enrichStat = <T extends RestaurantStat>(stat: T) => {
  const prev = previousStatsMap.value.get(stat.restaurant_id)
  const prevOrders = prev?.total_orders ?? 0
  const prevFailed = prev?.failed_transaction ?? 0
  const prevRatio = prev?.failed_transaction_ratio ?? '0%'
  return {
    ...stat,
    previous_total_orders: prevOrders,
    previous_failed_transaction: prevFailed,
    previous_failed_transaction_ratio: prevRatio,
    total_orders_diff: stat.total_orders - prevOrders,
    failed_transaction_diff: stat.failed_transaction - prevFailed,
    failed_ratio_diff: parsePercent(stat.failed_transaction_ratio) - parsePercent(prevRatio),
  }
}

// Sorting state for restaurant stats table
const sortColumn = ref<'restaurant_name' | 'total_orders' | null>(null)
const sortDirection = ref<'asc' | 'desc'>('asc')

const hasRestaurantId = (restaurant: RestaurantStat): restaurant is RestaurantStatWithId => {
  return restaurant.restaurant_id !== null
}

const sortedRestaurantStats = computed(() => {
  if (!dashboardData.value.restaurant_wise_stats) return []

  const sorted = dashboardData.value.restaurant_wise_stats.filter(hasRestaurantId)

  sorted.sort((a, b) => {
    let compareResult = 0

    if (sortColumn.value === 'restaurant_name') {
      compareResult = a.restaurant_name.localeCompare(b.restaurant_name)
    } else if (sortColumn.value === 'total_orders') {
      compareResult = a.total_orders - b.total_orders
    }

    return sortDirection.value === 'asc' ? compareResult : -compareResult
  })

  return sorted.map(enrichStat)
})

const restaurantStatsGrandTotal = computed(() => {
  const grandTotal = dashboardData.value.restaurant_wise_stats.find(
    (restaurant) => restaurant.restaurant_id === null
  )
  return grandTotal ? enrichStat(grandTotal) : undefined
})

const handleSort = (column: 'restaurant_name' | 'total_orders') => {
  if (sortColumn.value === column) {
    // Toggle direction if clicking the same column
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column and reset to ascending
    sortColumn.value = column
    sortDirection.value = 'asc'
  }
}

const getSortIcon = (column: 'restaurant_name' | 'total_orders') => {
  if (sortColumn.value !== column) return '↕'
  return sortDirection.value === 'asc' ? '↑' : '↓'
}

// Check if user should see the AI Platform section (hide from super_admin and partner roles)
const showAIPlatformSection = computed(() => {
  return !hasUserAnyRole(['super_admin', 'Partner'])
})


const stats = computed(() => {
  const current = dashboardData.value
  const previous = previousData.value
  return [
    {
      label: t('dashboard.stats.totalRevenue'),
      value: `${current.total_revenue.toFixed(2)}€`,
      previousValue: `${previous.total_revenue.toFixed(2)}€`,
      difference: formatDiff(current.total_revenue - previous.total_revenue, { decimals: 2, suffix: '€' }),
      color: 'green',
      metric: 'total_revenue',
    },
    {
      label: t('dashboard.stats.totalOrders'),
      value: current.total_orders.toString(),
      previousValue: previous.total_orders.toString(),
      difference: formatDiff(current.total_orders - previous.total_orders),
      color: 'blue',
      metric: 'total_orders',
    },
    {
      label: t('dashboard.stats.avgCheck'),
      value: `${current.average_check.toFixed(2)}€`,
      previousValue: `${previous.average_check.toFixed(2)}€`,
      difference: formatDiff(current.average_check - previous.average_check, { decimals: 2, suffix: '€' }),
      color: 'purple',
      metric: 'average_check',
    },
    {
      label: t('dashboard.stats.failedTransactions'),
      value: `${current.failed_transaction} / ${current.failed_transaction_ratio}`,
      previousValue: `${previous.failed_transaction} / ${previous.failed_transaction_ratio}`,
      difference: `${formatDiff(current.failed_transaction - previous.failed_transaction)} / ${formatDiff(parsePercent(current.failed_transaction_ratio) - parsePercent(previous.failed_transaction_ratio), { decimals: 2, suffix: '%' })}`,
      color: 'red',
      metric: 'failed_transaction',
    },
  ]
})

const fetchDashboardStats = async () => {
  if(!dateRange.value || dateRange.value.length !== 2) return
  isLoadingStats.value = true
  statsError.value = ''
  const [prevStart, prevEnd] = getPreviousPeriodRange(dateRange.value[0], dateRange.value[1])
  try {
    // Fetch current and previous period stats in parallel
    const [currentResponse, previousResponse] = await Promise.all([
      apiInstance.post('/user/dashboard-stats', {
        start_date: dateRange.value[0],
        end_date: dateRange.value[1],
      }),
      apiInstance.post('/user/dashboard-stats', {
        start_date: prevStart,
        end_date: prevEnd,
      }),
    ])
    if (!currentResponse.data.error) {
      dashboardData.value = currentResponse.data.data
    }
    previousData.value = previousResponse.data.error
      ? createEmptyStats()
      : previousResponse.data.data
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    statsError.value = 'Failed to load dashboard statistics'
  } finally {
    isLoadingStats.value = false
  }
}

// --- Daily chart state ---
type ChartMetric = 'total_revenue' | 'total_orders' | 'average_check' | 'failed_transaction'

const chartVisible = ref(false)
const chartMetric = ref<ChartMetric>('total_revenue')

const openChart = (metric: string) => {
  chartMetric.value = metric as ChartMetric
  chartVisible.value = true
}

const navigateToRestaurantOrders = (restaurantId: number) => {
  router.push({
    name: 'RestaurantDetail',
    params: { id: restaurantId },
    query: {
      tab: 'orders',
      start_date: dateRange.value[0],
      end_date: dateRange.value[1],
    },
  })
}

// Watch for language changes and re-run getDateRanges
watch(locale, () => {
  dateRanges.value = getDateRanges()
})

// Watch for date range changes and fetch stats
watch(dateRange, () => {
  fetchDashboardStats()
}, { deep: true })

onMounted(() => {
  fetchDashboardStats()
})

const recentActivities = computed(() => [
  {
    id: 1,
    user: 'Sarah Johnson',
    action: 'Created new account',
    time: '2 minutes ago',
    avatar: 'SJ',
    color: 'bg-green-500',
  },
  {
    id: 2,
    user: 'Mike Peters',
    action: 'Completed purchase',
    time: '1 hour ago',
    avatar: 'MP',
    color: 'bg-primary-500',
  },
  {
    id: 3,
    user: 'Emma Wilson',
    action: 'Updated profile',
    time: '1 hour ago',
    avatar: 'EW',
    color: 'bg-purple-500',
  },
  {
    id: 4,
    user: 'David Lee',
    action: 'Made a payment',
    time: '2 hours ago',
    avatar: 'DL',
    color: 'bg-orange-500',
  },
  {
    id: 5,
    user: 'Jessica Brown',
    action: 'Requested support',
    time: '3 hours ago',
    avatar: 'JB',
    color: 'bg-red-500',
  },
])

const performanceMetrics = computed(() => [
  {
    label: 'Server Uptime',
    value: '99.9%',
    color: 'bg-green-500',
    barWidth: 99.9,
  },
  {
    label: 'API Response Time',
    value: '145ms',
    color: 'bg-primary-500',
    barWidth: 72.5,
  },
  {
    label: 'Storage Usage',
    value: '68.5 GB / 100 GB',
    color: 'bg-orange-500',
    barWidth: 68.5,
  },
  {
    label: 'Bandwidth Usage',
    value: '42.3%',
    color: 'bg-cyan-500',
    barWidth: 42.3,
  },
])

const getCardColor = (color: string) => {
  const colors: Record<string, string> = {
    green: 'from-green-400 to-green-600',
    blue: 'from-primary-400 to-primary-600',
    purple: 'from-purple-400 to-purple-600',
    red: 'from-red-400 to-red-600',
  }
  return colors[color] || 'from-primary-400 to-primary-600'
}
</script>

<template>
  <div class="w-full max-w-7xl mx-auto">
    <!-- Page Header with Date Picker -->
    <div class="mb-8">
      <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">{{ t('dashboard.title') }}</h1>
          <p class="text-gray-600 text-sm mt-1">{{ t('dashboard.welcome') }}</p>
        </div>

        <!-- Date Picker Section (Inline) -->
        <div class="w-full md:w-auto">
          <!-- <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('dashboard.date_range') }}</label> -->

          <!-- Date Range Picker with Shortcuts (Element Plus) -->
          <el-date-picker
            size="small"
            v-model="dateRange"
            type="daterange"
            :range-separator="t('dashboard.date_range_separator')"
            :start-placeholder="t('dashboard.start_date')"
            :end-placeholder="t('dashboard.end_date')"
            format="DD.MM.YYYY"
            value-format="YYYY-MM-DD"
            :disabledDate="(date: Date) => date > new Date()"
            :shortcuts="shortcuts"
            class="w-full"
            :clearable="false"
          />
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="statsError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {{ statsError }}
      </div>
    </div>

    <!-- Stats Grid - 4 Columns -->
    <div v-if="isLoadingStats" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div
        v-for="i in 4"
        :key="`skeleton-${i}`"
        class="bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg p-6 text-white shadow-sm overflow-hidden relative animate-pulse"
      >
        <!-- Skeleton Content -->
        <div class="space-y-4">
          <div class="h-4 bg-gray-400 rounded w-1/2"></div>
          <div class="h-10 bg-gray-400 rounded w-3/4"></div>
          <div class="h-4 bg-gray-400 rounded w-1/2"></div>
        </div>
      </div>
    </div>
    <div v-if="!isLoadingStats" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div
        v-for="stat in stats"
        :key="stat.label"
        :class="['bg-gradient-to-br', getCardColor(stat.color), 'rounded-lg p-6 text-white shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden relative']"
        @click="openChart(stat.metric)"
      >
        <!-- Decorative Background Circle -->
        <div class="absolute -right-8 -top-8 w-32 h-32 opacity-10">
          <svg fill="currentColor" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="100" />
          </svg>
        </div>

        <!-- Content -->
        <div class="relative z-10 flex flex-col justify-between h-full">
          <!-- Label, current value and current period -->
          <div class="mb-5">
            <p class="text-white/80 text-sm font-medium mb-2">{{ stat.label }}</p>
            <p class="text-4xl font-bold text-white">{{ stat.value }}</p>
          </div>
        </div>
      </div>
    </div>


    <!-- Daily Trend Chart Modal -->
    <DashboardChartModal
      v-model:visible="chartVisible"
      :initial-date-range="dateRange"
      :metric="chartMetric"
    />

    <!-- Failed Transactions by Restaurant Skeleton -->
    <div v-if="isLoadingStats && !showAIPlatformSection" class="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="h-6 bg-gray-200 rounded animate-pulse w-1/3"></div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th class="px-6 py-3 text-left">
                <div class="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
              </th>
              <th class="px-6 py-3 text-right">
                <div class="h-4 bg-gray-200 rounded animate-pulse w-24 ml-auto"></div>
              </th>
              <th class="px-6 py-3 text-right">
                <div class="h-4 bg-gray-200 rounded animate-pulse w-32 ml-auto"></div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="i in 5" :key="`table-skeleton-${i}`" class="bg-gray-50">
              <td class="px-6 py-4">
                <div class="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
              </td>
              <td class="px-6 py-4 text-right">
                <div class="h-4 bg-gray-200 rounded animate-pulse w-12 ml-auto"></div>
              </td>
              <td class="px-6 py-4 text-right">
                <div class="h-6 bg-gray-200 rounded-full animate-pulse w-20 ml-auto"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Failed Transactions by Restaurant -->
    <div v-if="!isLoadingStats && dashboardData.restaurant_wise_stats && dashboardData.restaurant_wise_stats.length > 0 && !showAIPlatformSection && sortedRestaurantStats.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">{{ t('dashboard.failedTransactionsByRestaurant') }}</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th
                class="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                @click="handleSort('restaurant_name')"
              >
                <div class="flex items-center gap-2">
                  {{ t('dashboard.restaurantName') }}
                  <span class="text-gray-400">{{ getSortIcon('restaurant_name') }}</span>
                </div>
              </th>
              <th
                class="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                @click="handleSort('total_orders')"
              >
                <div class="flex items-center justify-center gap-2">
                  {{ t('dashboard.stats.totalOrders') }}
                  <span class="text-gray-400">{{ getSortIcon('total_orders') }}</span>
                </div>
              </th>
              <th class="px-6 py-3 text-right text-sm font-semibold text-gray-700">{{ t('dashboard.stats.failedTransactions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="restaurant in sortedRestaurantStats"
              :key="restaurant.restaurant_id"
              class="hover:bg-gray-50 transition-colors cursor-pointer"
              @click="navigateToRestaurantOrders(restaurant.restaurant_id)"
            >
              <td class="px-6 py-4 text-sm text-gray-900">{{ restaurant.restaurant_name }}</td>
              <td class="px-6 py-4 text-center">
                <div class="text-sm">
                  <span class="font-bold text-gray-900 ml-1.5">{{ restaurant.total_orders }}</span>
                </div>
              </td>
              <td class="px-6 py-4 text-right">
                <span :class="[
                  'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                  restaurant.failed_transaction === 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                ]">
                  {{ restaurant.failed_transaction }} / {{ restaurant.failed_transaction_ratio }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot
            v-if="restaurantStatsGrandTotal"
          >
            <tr class="bg-gray-50 border-t border-gray-200">
              <th class="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase transition-colors">
                {{ t('dashboard.stats.grandTotal') }}
              </th>
              <th class="px-6 py-3 text-center text-sm font-semibold text-gray-700 transition-colors">
                <div>
                  <span class="text-gray-400 font-normal">{{ t('dashboard.current') }}</span>
                  <span class="ml-1.5">{{ restaurantStatsGrandTotal.total_orders }}</span>
                </div>
              </th>
              <th class="px-6 py-2 text-right text-sm font-semibold text-gray-700 transition-colors">
                <span :class="[
                  'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                  restaurantStatsGrandTotal.failed_transaction === 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                ]">
                {{ restaurantStatsGrandTotal.failed_transaction }} /
                {{ restaurantStatsGrandTotal.failed_transaction_ratio }}
                </span>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- AI Platform Success Section Skeleton -->
    <div v-if="isLoadingStats && showAIPlatformSection" class="mb-8">
      <!-- Heading Skeleton -->
      <div class="h-16 bg-gray-200 rounded animate-pulse mb-10 w-3/4 mx-auto"></div>

      <!-- Three Feature Cards Skeleton -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-12 my-12">
        <div v-for="i in 3" :key="`ai-skeleton-${i}`" class="flex flex-col items-center text-center">
          <!-- Icon Skeleton -->
          <div class="w-40 h-40 bg-gray-200 rounded-lg animate-pulse mb-8"></div>

          <!-- Title Skeleton -->
          <div class="h-10 bg-gray-200 rounded animate-pulse mb-4 w-3/4"></div>

          <!-- Description Skeleton -->
          <div class="space-y-2 w-full">
            <div class="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div class="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
          </div>
        </div>
      </div>

      <!-- CTA Skeleton -->
      <div class="flex justify-center">
        <div class="h-12 bg-gray-200 rounded-xl animate-pulse w-96"></div>
      </div>
    </div>

    <!-- AI Platform Success Section -->
    <div v-if="!isLoadingStats && showAIPlatformSection" class="mb-8">
      <!-- Heading -->
      <h2 class="text-5xl text-gray-900 mb-10 leading-tight text-center">{{ t('dashboard.aiPlatform.title') }}</h2>

      <!-- Three Feature Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-12 my-12">
        <!-- Card 1: Analyse -->
        <div class="flex flex-col items-center text-center">
          <!-- Icon Container -->
          <div class="relative mb-8">
            <div class="w-40 h-40 flex items-center justify-center ml-10">
              <img src="/dashboard/analyse.png" alt="Analyse" class="w-full h-full object-contain">
            </div>
          </div>

          <!-- Title and Description -->
          <h3 class="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
            <div class="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-lg font-bold mr-5">
              1
            </div>
            <span>{{ t('dashboard.aiPlatform.analyse.title') }}</span>
          </h3>
          <p class="text-gray-600 text-base md:text-md leading-relaxed text-left ml-20">{{ t('dashboard.aiPlatform.analyse.description') }}</p>
        </div>

        <!-- Card 2: Potenzial -->
        <div class="flex flex-col items-center text-center">
          <!-- Icon Container -->
          <div class="relative mb-8">
            <div class="w-40 h-40 flex items-center justify-center">
              <img src="/dashboard/potential.png" alt="Potenzial" class="w-full h-full object-contain">
            </div>
          </div>

          <!-- Title and Description -->
          <h3 class="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
            <div class="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center text-lg font-bold mr-5">
              2
            </div>
            <span>{{ t('dashboard.aiPlatform.potential.title') }}</span>
          </h3>
          <p class="text-gray-600 text-base md:text-md leading-relaxed text-left ml-20">{{ t('dashboard.aiPlatform.potential.description') }}</p>
        </div>

        <!-- Card 3: Erfolg -->
        <div class="flex flex-col items-center text-center">
          <!-- Icon Container -->
          <div class="relative mb-8">
            <div class="w-40 h-40 flex items-center justify-center">
              <img src="/dashboard/success.png" alt="Erfolg" class="w-full h-full object-contain">
            </div>
          </div>

          <!-- Title and Description -->
          <h3 class="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
            <div class="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center text-lg font-bold mr-5">
              3
            </div>
            <span>{{ t('dashboard.aiPlatform.success.title') }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
          </h3>
          <p class="text-gray-600 text-base md:text-md leading-relaxed text-left ml-20">{{ t('dashboard.aiPlatform.success.description') }}</p>
        </div>
      </div>

      <!-- Bottom CTA Text -->
      <div class="flex justify-center">
        <div class="bg-gray-50 border border-gray-300 rounded-xl p-4 w-fit">
          <p class="text-gray-900 text-2xl">
            <span class="font-semibold">{{ t('dashboard.aiPlatform.cta_bold') }}</span>
            <span>{{ t('dashboard.aiPlatform.cta') }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Two Column Layout: Recent Activity + Performance -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 hidden">
      <!-- Recent Activity - Left Column -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>
        <div class="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          <div
            v-for="activity in recentActivities"
            :key="activity.id"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="flex items-start gap-3">
              <!-- Avatar -->
              <div :class="[activity.color, 'w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0']">
                {{ activity.avatar }}
              </div>
              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ activity.user }}</p>
                <p class="text-xs text-gray-600">{{ activity.action }}</p>
                <p class="text-xs text-gray-500 mt-1">{{ activity.time }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Performance Overview - Right Column -->
      <div class="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Performance Overview</h2>
        </div>
        <div class="p-6">
          <div class="space-y-6">
            <div v-for="metric in performanceMetrics" :key="metric.label">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">{{ metric.label }}</span>
                <span class="text-sm font-semibold text-gray-900">{{ metric.value }}</span>
              </div>
              <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  :class="[metric.color, 'h-full rounded-full transition-all']"
                  :style="{ width: `${metric.barWidth}%` }"
                ></div>
              </div>
            </div>
          </div>
          <div class="mt-6 pt-6 border-t border-gray-200">
            <p class="text-xs text-gray-500">Last updated: Just now</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Remove default scrollbar styling and apply custom */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
